'use client'

/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useRef } from 'react'
import OverlayModal from '@components/OverlayModal/overlayModal'
import { ModalType } from '@enums/ModalType'
import { pageEvent, waitForAnalyticsLoad } from '@acko-tech/building-blocks.ui.acko-analytics'
import Image from 'next/image'
import QRCode from 'react-qr-code'
import { SEGMENT } from '@components/MainWidget/common/widgetSegments'
import { MODAL_CROSS_ICON } from '@constants/homepageLogos'
import { ModalItem } from '@interfaces/landingWidget'
import { DASH_FALLBACK } from '@components/MainWidget/common/commonConstants'
import { LOW_BALANCE_TEXT, SUFFICIENT_BALANCE_TEXT } from '@components/MainWidget/FastagWidget/constants'
import { CHALLAN_WIDGET_LANDING } from '@components/Product/ProductWidget/constants'
import { widgetJourney, fetchWidgetProductFn } from '@configs/segmentEvents.config'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import { fastagBalanceContainerData } from '@helpers/fastagBalanceHelper'
import styles from './ProductModal.module.scss'

interface ProductModalProps {
  modalData: ModalItem
  show: boolean
  setShow: Function
  vasWrapperProps?: any
  downloadUrl?: string
  vehicleInfoData?: any
  segmentEventCommonProperties?: any
  regNum?: string
  mobileNo?: string
  newVasLandingFlow?: boolean
  widgetType?: string
  isBottomFloater?: boolean
}

const vehicleTypeMap = {
  PRIVATE_CAR: 'Car',
  TWO_WHEELER: 'Bike',
}

const vehicleType = vehicleData => vehicleTypeMap?.[vehicleData?.vehicleInfo?.vehicle_type_v2] || 'N/A'

export const ProductModal = ({
  modalData,
  show,
  setShow,
  vasWrapperProps,
  downloadUrl,
  vehicleInfoData,
  segmentEventCommonProperties,
  regNum,
  mobileNo,
  newVasLandingFlow = false,
  widgetType = CHALLAN_WIDGET_LANDING,
  isBottomFloater = false,
}: ProductModalProps) => {
  const hasFiredRef = useRef<boolean>(false)

  useEffect(() => {
    if (newVasLandingFlow && !hasFiredRef.current) {
      waitForAnalyticsLoad().then(() => {
        pageEvent(SEGMENT.VIEW_PAGE, {
          ...segmentEventCommonProperties,
          ...(newVasLandingFlow && { journey: widgetJourney[widgetType] }),
          product: fetchWidgetProductFn[widgetType]?.(),
          phone: mobileNo || '',
          registration_number: regNum || '',
          vehicle_type: vehicleType(vehicleInfoData),
          mm_name: vehicleInfoData?.vehicleInfo?.model_name || '',
          previous_policy_expiry_date: vehicleInfoData?.vehicleInfo?.previous_policy_expiry_detail || '',
          expiry_date: vehicleInfoData?.vehicleInfo?.previous_policy_expiry_detail || '',
          vas_amount: null,
        })
        hasFiredRef.current = true
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newVasLandingFlow])

  const { hasBalanceContainer, balanceValue, isBalanceLow } = fastagBalanceContainerData(vehicleInfoData)

  const modalContent = (
    <div className={styles.modalWrapper}>
      <div className={styles.contentWrapper}>
        <PropertyControlledComponent controllerProperty={hasBalanceContainer} fallback={null}>
          <div className={styles.balanceBanner}>
            <div className={styles.balanceContent}>
              <div className={`${styles.balanceStatus} ${isBalanceLow ? styles.lowBalance : ''}`}>
                {isBalanceLow ? LOW_BALANCE_TEXT : SUFFICIENT_BALANCE_TEXT}
              </div>
              <h3 className={styles.balance}>₹{Number.isFinite(balanceValue) ? balanceValue : DASH_FALLBACK}</h3>
            </div>
            <Image
              src={vehicleInfoData?.vehicleImage || modalData?.carImageSrc}
              alt="Balance image"
              className={styles.media}
              width={90}
              height={48}
            />
          </div>
        </PropertyControlledComponent>
        <h3 className={`${styles.modalHeading} ${newVasLandingFlow ? styles.extraWidth : ''}`}>
          {vasWrapperProps?.challanDesktopHeading || modalData?.heading}
        </h3>
        <span className={styles.modalSubHeading}>
          {vasWrapperProps?.challanDesktopDescription || modalData?.subHeading}
        </span>
        <div className={styles.modalCards}>
          <div className={styles.smallCards}>
            {(vasWrapperProps?.challanKeyFeaturesDesktop || modalData?.cards)?.map(cardItem => (
              <div className={styles.card} key={cardItem?.title || cardItem?.text}>
                <Image
                  src={cardItem?.src || cardItem?.logoUrl}
                  alt={cardItem?.title || cardItem?.text}
                  width={40}
                  height={40}
                  className={styles.cardImage}
                />
                <span className={styles.cardText}>{cardItem?.title || cardItem?.text}</span>
              </div>
            ))}
          </div>
          <div className={styles.bigCard}>
            <div className={styles.content}>
              <span className={styles.heading}>
                {vasWrapperProps?.challanDownloadDesktopApp?.title || modalData?.qrHeading}
              </span>
              <span className={styles.subHeading}>
                {vasWrapperProps?.challanDownloadDesktopApp?.subTitle || modalData?.qrSubHeading}
              </span>
            </div>
            <QRCode className={styles.qrImage} value={downloadUrl || modalData?.qrImageSrc} size={120} />
          </div>
        </div>
      </div>
      <div
        className={styles.modalMediaWrapper}
        style={{ backgroundImage: `url(${vasWrapperProps?.bgBlurImageUrl || modalData?.bgBlurImageUrl})` }}>
        <div
          className={`${styles.modalMediaFrame} ${hasBalanceContainer ? styles.hasBalanceBanner : ''}`}
          style={{ backgroundImage: `url(${modalData?.bgHandImageUrl})` }}>
          <div className={styles.modalMediaStyle}>
            {modalData?.hasVideo ? (
              <video className={styles.media} autoPlay loop muted playsInline>
                <source src={modalData?.mediaSrc} type="video/mp4" />
                {modalData?.name}
              </video>
            ) : (
              <Image
                width={500}
                height={500}
                className={styles.media}
                alt="Modal mobile image"
                src={vasWrapperProps?.mediaSrc || modalData?.mediaSrc}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return newVasLandingFlow && isBottomFloater ? (
    modalContent
  ) : (
    <OverlayModal show={show} setShow={setShow} modalType={ModalType.LARGE} crossIconSrc={MODAL_CROSS_ICON}>
      {modalContent}
    </OverlayModal>
  )
}
